import { getCountryFromLocale } from "@/graphql/schemas/shared/utils/getCountryFromLocale"
import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { getSkuPrice } from "@/graphql/schemas/shared/utils/getPrices"
import { getStoreKeysWithChannels } from "@/graphql/schemas/shared/utils/getStoreKeysWithChannels"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"

export const queryCustomerSkuPrices = {
  async skuCustomerPrices(parent, args, context, info) {
    const { customerId, customerKey, customerNumber, sku, country } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRICES,
      operation: Operations.READ,
    })

    const customer = await getCustomer({ id: customerId, key: customerKey, number: customerNumber })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    let skuPrices = await getCustomerSkuPrices({
      customerId,
      customerKey,
      customerNumber,
      sku,
      country,
    })

    skuPrices.oldPrices = await getLastOrderPrices(sku, customerNumber)

    return skuPrices
  },
}

export const getCustomerSkuPrices = async ({ customerId, customerKey, customerNumber, sku, country }) => {
  const customer = await getCustomer({ id: customerId, key: customerKey, number: customerNumber })

  if (!customer) return null

  const { stores, customerGroup } = customer
  const channels = await getStoreKeysWithChannels(stores)

  // to cover the case when country is sent like this: "de-DE"
  const extractedCountry = getCountryFromLocale(country)

  const prices = await Promise.all(
    sku.map(async (item) => {
      const price = await getSkuPrice({
        sku: item,
        channels,
        group: customerGroup,
        country: extractedCountry,
      })

      return price
    }),
  )
  if (prices && prices.length >= 1) return { total: prices.length, items: prices }

  return null
}

const getLastOrderPrices = async (skus, customerNumber) => {
  const lastOrderPriceQuery = getLastOrderPricesQuery(skus, customerNumber)

  const lastPrice = (await EinzaGraphiQLService.getData(lastOrderPriceQuery))?.data
  let lastPriceReturn = []
  skus.forEach((skuValue) => {
    if (lastPrice[`price_${skuValue}`]?.content?.length > 0) {
      let tmp = lastPrice[`price_${skuValue}`].content[0]
      if (tmp?.items?.length > 0) {
        const discount = tmp.items.filter((item) => item?.sku === skuValue.toString())[0]?.discount
        const price = tmp.items.filter((item) => item?.sku === skuValue.toString())[0]?.price

        lastPriceReturn.push({
          orderNumber: tmp.orderType + "-" + tmp.orderNumber2,
          price: price,
          discount: discount,
          discountPrice: price * ((100 - discount) / 100),
          sku: skuValue,
          orderDate: tmp.orderDate,
          customerOrderText: tmp.customerOrderText,
        })
      }
    }
  })
  return lastPriceReturn
}

const getLastOrderPricesQuery = (skus, customerNumber) => {
  let queryList = []

  const filterValues = (sku) => {
    return [
      { field: "item.sku", operator: "eq", value: sku.toString() },
      { field: "orderStatus", operator: "in", value: "F" },
      { field: "orderType", operator: "in", value: "AU;BA" },
    ]
  }
  skus.forEach((sku) => {
    const graphQlQueryObject = {
      method: `price_${sku} : findOrdersByCustomerAndFilter`,
      variables: {
        customerNumber: customerNumber,
        filters: filterValues(sku),
        page: 0,
        size: 10,
        sort: "orderDate.desc",
      },
      fields: ["numberOfElements"],
      expandedFields: {
        content: ["orderNumber2", "orderType", "orderDate", "customerOrderText", "items {\nsku\nprice\ndiscount\n}"],
      },
    }
    queryList.push(graphQlQueryObject)
  })

  return EinzaGraphiQLService.formQuery(queryList)
}
